import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/utils/classname';

export interface ConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when user cancels (Cancel button, Escape key) */
  onClose: () => void;
  /** Called when user confirms the action */
  onConfirm: () => void;
  /** Dialog title (e.g., "Delete API Key") */
  title: string;
  /** Description text explaining consequences */
  description: string;
  /** Label for the confirm button (e.g., "Delete", "Remove", "Disconnect") */
  confirmLabel?: string;
  /** Label for the cancel button */
  cancelLabel?: string;
  /** Visual variant — destructive shows red confirm button */
  variant?: 'default' | 'destructive';
  /** Whether the action is in progress */
  isLoading?: boolean;
  /** Loading text shown on confirm button while isLoading */
  loadingLabel?: string;
  /** Optional icon to show (defaults to ExclamationTriangleIcon for destructive) */
  icon?: React.ReactNode;
  /** Optional data-testid for the dialog */
  'data-testid'?: string;
}

export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'destructive',
  isLoading = false,
  loadingLabel,
  icon,
  'data-testid': testId,
}: ConfirmationDialogProps) {
  const isDestructive = variant === 'destructive';

  const resolvedIcon =
    icon !== undefined ? (
      icon
    ) : isDestructive ? (
      <ExclamationTriangleIcon className="size-6 text-amber-500" />
    ) : null;

  const confirmButtonLabel =
    isLoading && loadingLabel ? loadingLabel : confirmLabel;

  return (
    <AlertDialog open={open} onOpenChange={open => !open && onClose()}>
      <AlertDialogContent
        data-testid={testId}
        className={cn(
          'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary max-w-md rounded-lg border-2 [&>button]:hidden'
        )}
      >
        <AlertDialogHeader>
          <div className="flex items-start gap-4">
            {resolvedIcon && (
              <div className="mt-0.5 shrink-0">{resolvedIcon}</div>
            )}
            <div className="flex flex-col gap-1">
              <AlertDialogTitle className="text-chariot-text-primary text-lg leading-none font-semibold tracking-tight">
                {title}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-chariot-text-secondary text-sm">
                {description}
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={isLoading}
            className={cn(buttonVariants({ variant: 'ghost' }), 'mt-2 sm:mt-0')}
          >
            {cancelLabel}
          </AlertDialogCancel>

          <AlertDialogAction
            onClick={onConfirm}
            disabled={isLoading}
            autoFocus={false}
            className={cn(
              isDestructive
                ? buttonVariants({ variant: 'destructive' })
                : buttonVariants({ variant: 'default' })
            )}
          >
            {confirmButtonLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
